import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { CategoryItems, LocationItems } from '../services/activities';
import { FaStar } from "react-icons/fa";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { logEvent } from '../utils/analytics';

const TicketsWrapper = styled.div`
  background: ${({ isModalOpen }) => (isModalOpen ? "rgba(0, 0, 0, 0.5)" : "#EFF2F4")};
  padding: 10px 20px;
  transition: background 0.3s ease-in-out;
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 3;
  display: ${({ $show }) => ($show ? "block" : "none")};
`;

const TicketOptions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 10px auto;
`;

const TicketOptionsDetail = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;

  .ticket_input {
    display: flex;
    align-items: center;
    background: #ffffff;
    border: 1px solid #d3d3d3;
    border-radius: 8px;
    padding: 10px;
    font-size: 14px;
    color: #3e3e3e;
    cursor: pointer;

    input, select {
      border: none;
      outline: none;
      background: transparent;
      color: inherit;
      font-weight: bold;
    }

    &:hover {
      border-color: #1B56ED;
    }
  }
`;

const TicketSort = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  label {
    font-size: 14px;
    font-weight: 500;
    color: #3e3e3e;
  }

  select {
    background: white;
    border: 1px solid #d3d3d3;
    border-radius: 8px;
    padding: 5px 10px;
    font-size: 14px;
    color: #3e3e3e;
    outline: none;
    cursor: pointer;

    &:hover {
      border-color: #1B56ED;
    }
  }
`;

const UpdateButton = styled.button`
  background: #1B56ED;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  text-align: center;
  color: white;
  font-weight: bold;
  cursor: pointer;
`;

const TicketsList = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const TicketItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  background: white;
  margin-top: 20px;
  border-radius: 12px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  cursor: pointer;
`;

const TicketImage = styled.img`
  width: 32%;
  object-fit: cover;
`;

const TicketContent = styled.div`
  width: 65%;
  padding: 8px 10px;

  .title {
    color: black;
    font-size: 16px;
    font-weight: bold;
  }

  .info {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    color: black;
    font-size: 14px;
    font-weight: 400;

    .rate {
      display: flex;
      flex-direction: row;
      gap: 2px;
    }
  }

  .ticket-rate-summary {
    color: #8A8A8A;
    font-size: 12px;
    margin: 5px 0;
  }

  .MoreInfo {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-end;

    div {
      margin-right: 5px;
      color: #1B56ED;
      font-size: 11px;
    }

    button {
      background: #1B56ED;
      border: none;
      border-radius: 8px;
      padding: 5px 8px;
      text-align: center;
      color: white;
      font-size: 14px;
      font-weight: bolder;
      cursor: pointer;

      &:hover {
        background: #1546c2;
      }
    }
  }
`;

const PriceTable = styled.div`
  margin: 10px 0;

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 15px; 
    padding: 5px 0;
    border-top: 1px solid #e0e0e0;
  }

  .seller {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 14px;

    img {
      height: 100%;
      max-height: 15px;
      width: auto;
    }
  }

  .price {
    font-size: 14px;
    font-weight: bold;
  }
`;

const ShowMoreButton = styled.button`
  margin-top: 10px;
  padding: 8px 16px;
  border: none;
  background-color: #007BFF;
  color: white;
  border-radius: 18px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50%;
  background: white;
  box-shadow: 0px -2px 6px rgba(0, 0, 0, 0.1);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 40px 0;
  z-index: 5;
  display: ${({ show }) => (show ? "block" : "none")};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  .title {
    margin: 0 20px;
    font-size: 20px;
    font-weight: bold;
  }

  .description {
    margin: 30px;
    font-size: 18px;
  }
`;

const Tickets = ({ location, category, searchKeyword }) => {
  const navigate = useNavigate();
  const [sortOption, setSortOption] = useState("Price (low to high)");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedPersonType, setSelectedPersonType] = useState("All");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false)
  const itemsPerPage = 10;  

  const handleSortChange = (e) => setSortOption(e.target.value);
  const handleTicketClick = (id) => {
    logEvent("select_item", {
      item_id: id,
      item_name: tickets.find(ticket => ticket.id === id)?.name,
      price: tickets.find(ticket => ticket.id === id)?.ticketPreview?.[0]?.price,
      currency: "USD"
    });
    navigate(`/items/${id}`);
  };
  const handleShowMoreItems = () => {
    logEvent("view_more_items", {
      list_name: "ticket_list",
      page_number: currentPage + 1
    });
    setCurrentPage((prevPage) => prevPage + 1);
  };
  const handleDateChange = (e) => setSelectedDate(e.target.value);
  const handlePersonTypeChange = (e) => setSelectedPersonType(e.target.value);

  const handleUpdate = useCallback(async () => {
    setLoading(true);
    try {
      const requestData = { selectedDay: selectedDate, option: selectedPersonType };
  
      const response = category
        ? await CategoryItems(location, category, requestData)
        : await LocationItems(location, requestData);
  
      setTickets(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [category, location, selectedDate, selectedPersonType]);

  const filteredTickets = tickets.filter(ticket => {
    if (!searchKeyword) return tickets;
    const keyword = searchKeyword.trim().toLowerCase();

    return (
      ticket.name.toLowerCase().includes(keyword) ||
      ticket.description?.toLowerCase().includes(keyword) ||
      (Array.isArray(ticket.categories) &&
        ticket.categories.some(cat => cat.toLowerCase().includes(keyword))) ||
      ticket.city.toLowerCase().includes(keyword) ||
      ticket.state.toLowerCase().includes(keyword)
    );
  });

  useEffect(() => {
    handleUpdate();
  }, [handleUpdate]);  

  const openTooltipModal = () => {
    setIsModalOpen(true);
  };

  const closeTooltipModal = () => {
    setIsModalOpen(false);
  };

  // const sortedTickets = [...tickets].sort((a, b) => {
  //   switch (sortOption) {
  //     case "Price (low to high)":
  //       return Object.values(a.ticket)[0] - Object.values(b.ticket)[0];
  //     case "Price (high to low)":
  //       return Object.values(b.ticket)[0] - Object.values(a.ticket)[0];
  //     // case "Best reviewed":
  //     //   return b.rate.rate - a.rate.rate;
  //     case "Recently added":
  //       return b.id - a.id;
  //     // case "Most popular":
  //     //   return b.rate.num - a.rate.num;
  //     default:
  //       return 0;
  //   }
  // });

  const sortedTickets = [...filteredTickets].sort((a, b) => {
    switch (sortOption) {
      case "Price (low to high)":
        return (a.ticketPreview?.[0]?.price ?? Infinity) - (b.ticketPreview?.[0]?.price ?? Infinity);
      case "Price (high to low)":
        return (b.ticketPreview?.[0]?.price ?? 0) - (a.ticketPreview?.[0]?.price ?? 0);
      case "Recently added":
        return (b.id ?? "").localeCompare(a.id ?? "");
      default:
        return 0;
    }
  });
  const visibleTickets = sortedTickets.slice(0, currentPage * itemsPerPage);

  const getImageSrc = (imageData) => {
    if (!imageData || imageData === "[]" || imageData.length === 0) return "/assets/tempAct.jpg";
    try {
      const parsedImage = typeof imageData === "string" ? JSON.parse(imageData) : imageData;
      if (Array.isArray(parsedImage) && parsedImage.length > 0) {
        return parsedImage[0];
      }
    } catch (e) {
      console.warn("Image parsing error:", e);
    }
    return "/assets/tempAct.jpg";
  };

  const getPlatformImage = (platformName) => {
    const platformImages = {
      "booking.com": "/assets/Logo/Bookingcom.png",
      "trip.com": "/assets/Logo/Tripcom.png",
      "klook": "/assets/Logo/Klook.jpg",
      "tiqets": "/assets/Logo/Tiqets.jpg",
      "viator": "/assets/Logo/Viator.jpg"
    };
  
    return platformImages[platformName.toLowerCase()] || null;
  };

  return (
    <>
      <Backdrop $show={isModalOpen} onClick={closeTooltipModal} />
      <TicketsWrapper $isModalOpen={isModalOpen}>
        <TicketOptions>
          <TicketOptionsDetail>
            <div className="ticket_input">
              <input type="date" value={selectedDate} onChange={handleDateChange} />
            </div>
            <div className="ticket_input">
              <select
                value={selectedPersonType}
                onChange={handlePersonTypeChange}
              >
                <option value="All"> All </option>
                <option value="Adult"> Adult </option>
                <option value="Child"> Child </option>
              </select>
            </div>
          </TicketOptionsDetail>
          <UpdateButton onClick={handleUpdate}>Update</UpdateButton>
        </TicketOptions>
        <TicketSort>
          <label htmlFor="sort">Sort by:</label>
          <select id="sort" value={sortOption} onChange={handleSortChange}>
            <option value="Price (low to high)">Price (low to high)</option>
            <option value="Price (high to low)">Price (high to low)</option>
            {/* <option value="Best reviewed">Best reviewed</option> */}
            <option value="Recently added">Recently added</option>
            {/* <option value="Most popular">Most popular</option> */}
          </select>
        </TicketSort>
        <TicketsList>
          { loading ? (
            <p>Loading...</p>
            ) : error ? (
              <p>Error: {error}</p>
            ) : sortedTickets.length === 0 ? (
              <p>No tickets available</p>
            ) : (
              visibleTickets.map((ticket) => (
                <TicketItem key={ticket.id}>
                  <TicketImage src={getImageSrc(ticket.image)} alt={ticket.name} />
                  <TicketContent>
                    <div className="title">{ticket.name}</div>
                    <div className="info">
                      <div>{ticket.city}</div>
                      {/* <div>⭐ {ticket.rate.rate} ({ticket.rate.num})</div> */}
                      <div className='rate'>
                        <div style={{ color: '#1B56ED' }}><FaStar /> </div>
                        <div style={{ color: 'gray' }} onClick={openTooltipModal}><IoIosInformationCircleOutline /></div>
                      </div>
                    </div>
                    {/* <div className="ticket-rate-summary">💬 {ticket.rate.sum}</div>? */}
                    <div className="ticket-rate-summary">💬 Good!!</div>
                    <PriceTable>
                      {(ticket.ticketPreview ?? []).slice(0, 2).map((preview, index) => (
                        <div className="row" key={index}>
                          <div className="seller">
                          {getPlatformImage(preview.platformName) ? (
                            <img src={getPlatformImage(preview.platformName)} alt={preview.platformName} />
                          ) : (
                            preview.platformName
                          )}
                          </div>
                          <div className="price">${preview.price?.toFixed(2)}</div>
                        </div>
                      ))}
                      {ticket.ticketPreview.length > 2 && (
                        <div className="row">
                          <div className="seller">
                            <strong>{ticket.ticketPreview[2].platformName}</strong> and more
                          </div>
                        </div>
                      )}          
                    </PriceTable>
                    <div className="MoreInfo">
                      <div>{ticket.ticketTypeCount}+ types of tickets</div>
                      <button onClick={() => handleTicketClick(ticket.id)}>View Prices</button>
                    </div>
                  </TicketContent>
                </TicketItem>
              ))
            )
          }
          {currentPage * itemsPerPage < filteredTickets.length && (
            <ShowMoreButton onClick={handleShowMoreItems}>More Items</ShowMoreButton>
          )}
        </TicketsList>
      </TicketsWrapper>

      <ModalOverlay show={isModalOpen}>
        <ModalContent>
          <p className='title'><FaStar color='#fbc02d'/> We are in the process of introducing a rating system <FaStar color='#fbc02d' /></p>
          <p className='description'>You can check the ratings and reviews for this product here.</p>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default Tickets;